import Autocomplete from "@mui/material/Autocomplete";
import {
  FieldWrapper,
  FormTextField,
  compactInlineAutocompleteSx,
  formAutocompleteInputSx,
  formInlinePlaceholderSx,
} from "styles/formStyleComponents";
import InfoIconWithTooltip from "./InfoIconWithTooltip";
import { Box } from "@mui/material";

const SingleSelectFieldComponent = ({
  name,
  label,
  placeholder,
  value,
  required,
  disabled,
  error,
  info,
  options = [],
  onOpen,
  onChange,
  compact = false,
  inline = false,
  disableClearable = false,
}) => {
  const fieldLabel = required ? `${label} *` : label;

  const optionValuesMatch = (a, b) => {
    if (a === b) {
      return true;
    }
    if (a == null || b == null) {
      return false;
    }
    return String(a) === String(b);
  };

  const getOptionFromValue = (val) => {
    if (!val && val !== 0) return null;
    return (
      options.find(
        (opt) => optionValuesMatch(opt?.value, val) || opt === val
      ) || null
    );
  };

  const selectedOption = getOptionFromValue(value);
  const isCompactInline = compact && inline;
  const usePlaceholderWhenEmpty = Boolean(placeholder) && !isCompactInline;
  const hasValue = selectedOption != null;
  const showEmptyPlaceholder =
    !hasValue && (isCompactInline || usePlaceholderWhenEmpty);
  const showFloatingLabel = isCompactInline
    ? false
    : usePlaceholderWhenEmpty
      ? hasValue
      : true;
  const inputPlaceholder = showEmptyPlaceholder
    ? placeholder || fieldLabel
    : placeholder;
  const autocompleteSx = isCompactInline
    ? {
        ...compactInlineAutocompleteSx,
        ...(showEmptyPlaceholder && {
          "& .MuiAutocomplete-input::placeholder": (theme) => ({
            opacity: 1,
            ...formInlinePlaceholderSx(theme, { error: !!error }),
          }),
        }),
      }
    : formAutocompleteInputSx;

  return (
    <FieldWrapper compact={compact} inline={inline}>
      <Autocomplete
        sx={autocompleteSx}
        disableClearable={disableClearable}
        slotProps={{
          htmlInput: { autoComplete: "off" },
        }}
        value={selectedOption}
        onChange={(_, newValue) => {
          if (disableClearable && newValue == null) {
            return;
          }
          const optionValue = newValue?.value !== undefined ? newValue.value : newValue;
          onChange(name, optionValue);
        }}
        options={options}
        disabled={disabled}
        onOpen={onOpen}
        getOptionLabel={(option) => option?.label || option || ""}
        isOptionEqualToValue={(option, val) => {
          if (!val) return false;
          if (option === val) return true;
          return (
            optionValuesMatch(option?.value, val?.value) ||
            optionValuesMatch(option?.value, val)
          );
        }}
        renderOption={(props, option) => {
          const isClearSelection = option?.value === -1;
          return (
            <Box
              component="li"
              {...props}
              sx={{
                ...props.sx,
                ...(isClearSelection && { color: "#6f6e6e" }),
              }}
            >
              {option?.label || option || ""}
            </Box>
          );
        }}
        renderInput={(params) => (
          <FormTextField
            {...params}
            label={showFloatingLabel ? fieldLabel : undefined}
            placeholder={inputPlaceholder}
            size="small"
            error={!!error}
            helperText={error}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: showFloatingLabel,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  <InfoIconWithTooltip info={info} />
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </FieldWrapper>
  );
};

export default SingleSelectFieldComponent;

